import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import apiService from "../../../service/Api";
import {
  BugReport,
  BugReportPayload,
  BugDashboardResponse, BugAssignment, BugAssignmentResponse,BugAdminDashboardResponse
} from "./types";

/* =========================
   State Types
========================= */

interface BugReportRes {
  results: BugReport[];
  next: string | null;
  prev: string | null;
  count: number;
}

interface BugState {
  bugs: BugReport[];

  // dashboard
  dashboard: BugDashboardResponse | null;
  adminDashboard: BugAdminDashboardResponse | null;
  assignments: BugAssignment[];
  loading: boolean;
  error: string | null;
  success: boolean;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { message?: string } } })
      .response;
    return response?.data?.message || fallback;
  }
  return fallback;
};

/* =========================
   Initial State
========================= */

const initialState: BugState = {
  bugs: [],
  assignments: [],
  dashboard: null,
  adminDashboard: null,
  loading: false,
  error: null,
  success: false,
};

/* =========================
   ASYNC THUNKS
========================= */

// CREATE bug report
export const createBugReport = createAsyncThunk<
  BugReport,
  BugReportPayload,
  { rejectValue: string }
>("bugs/create", async (payload, { rejectWithValue }) => {
  try {
    const response = await apiService.post<BugReport>(
      "/api/bug-report/bugs/",
      payload
    );
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(
      getErrorMessage(error, "Failed to submit bug report")
    );
  }
});

// FETCH all bugs (list)
export const fetchBugs = createAsyncThunk<
  BugReportRes,
  void,
  { rejectValue: string }
>("bugs/fetchAll", async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.get<BugReportRes>(
      "/api/bug-report/bugs/"
    );
    return response.data;
  } catch {
    return rejectWithValue("Failed to fetch bugs");
  }
});

// FETCH dashboard data
export const fetchBugDashboard = createAsyncThunk<
  BugDashboardResponse,
  void,
  { rejectValue: string }
>("bugs/fetchDashboard", async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.get<BugDashboardResponse>(
      "/api/bug-report/bugs/dashboard/"
    );
    return response.data;
  } catch {
    return rejectWithValue("Failed to load bug dashboard");
  }
});

export const fetchBugAdminDashboard = createAsyncThunk<
  BugAdminDashboardResponse,
  void,
  { rejectValue: string }
>("bugs/fetchAdminDashboard", async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.get<BugAdminDashboardResponse>(
      "/api/bug-report/bugs/admin-dashboard/"
    );
    return response.data;
  } catch {
    return rejectWithValue("Failed to load admin bug dashboard");
  }
});


export const fetchBugAssignments = createAsyncThunk<
  BugAssignmentResponse,
  number,
  { rejectValue: string }
>("bugs/fetchAssignments", async (userId, { rejectWithValue }) => {
  try {
    const response = await apiService.get<BugAssignmentResponse>(
      `/api/bug-report/bug-assignments/?assigned_to=${userId}&&page_size=100`
    );
    return response.data;
  } catch {
    return rejectWithValue("Failed to fetch bug assignments");
  }
});

export const fetchAdminBugAssignments = createAsyncThunk<
  BugAssignmentResponse,
  void,
  { rejectValue: string }
>("bugs/fetchAssignments", async (_, { rejectWithValue }) => {
  try {
    const response = await apiService.get<BugAssignmentResponse>(
      `/api/bug-report/bug-assignments/?page_size=100`
    );
    return response.data;
  } catch {
    return rejectWithValue("Failed to fetch bug assignments");
  }
});

/* =========================
   SLICE
========================= */

const bugSlice = createSlice({
  name: "bugs",
  initialState,
  reducers: {
    resetBugState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---------- CREATE BUG ---------- */
      .addCase(createBugReport.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        createBugReport.fulfilled,
        (state, action: PayloadAction<BugReport>) => {
          state.loading = false;
          state.success = true;
          state.bugs.unshift(action.payload);
        }
      )
      .addCase(createBugReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      /* ---------- FETCH BUG LIST ---------- */
      .addCase(fetchBugs.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchBugs.fulfilled,
        (state, action: PayloadAction<BugReportRes>) => {
          state.loading = false;
          state.bugs = action.payload.results;
        }
      )
      .addCase(fetchBugs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load bugs";
      })

      /* ---------- FETCH DASHBOARD ---------- */
      .addCase(fetchBugDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchBugDashboard.fulfilled,
        (state, action: PayloadAction<BugDashboardResponse>) => {
          state.loading = false;
          state.dashboard = action.payload;
        }
      )
      .addCase(fetchBugDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load dashboard";
      })
      /* ---------- FETCH ADMIN DASHBOARD ---------- */
      .addCase(fetchBugAdminDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchBugAdminDashboard.fulfilled,
        (state, action: PayloadAction<BugAdminDashboardResponse>) => {
          state.loading = false;
          state.adminDashboard = action.payload;
        }
      )
      .addCase(fetchBugAdminDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load admin dashboard";
      })
      .addCase(fetchBugAssignments.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchBugAssignments.fulfilled,
        (state, action: PayloadAction<BugAssignmentResponse>) => {
          state.loading = false;
          state.assignments = action.payload.results;
        }
      )
      .addCase(fetchBugAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load assignments";
      });

  },
});

export const { resetBugState } = bugSlice.actions;
export default bugSlice.reducer;
